import { defaultIfEmpty, isEmpty, right } from '@/utils/stringUtils';
import * as wecom from '@wecom/jssdk';
import React from 'react';
import { Updater } from 'use-immer';
import api from '../api';
import { useRequest, useRequestImmer } from '../api/hooks';
import { ID } from '../global';
import { CardUser } from './card';
import { Page } from './common';
import { Coupon } from './coupon';
import { Order } from './order';
import { Point } from './point';

export const TEMP_USER_NAME = '未转化客户';

export const UserEndpoint = `/user`;

export const VipEndpoint = `/vip`;

export const UserFullInfoContext = React.createContext<{
  userFullInfo: UserFullInfo;
  updateUserFullInfo: Updater<UserFullInfo>;
}>({
  userFullInfo: null,
  updateUserFullInfo: () => { },
});

export interface UserFullInfo {
  user: User;
  couponUserList: Coupon[];
  cardList: CardUser[];
  uas: number;
  lso: Pick<Order, 'orderSn' | 'endTime'>;
  // totalConsumption: number;
  // cardUserNum: number;
}

enum UserStatusEnum {
  NORMAL = 0,
  DISABLED = 1,
  TEMPORARY = 2,
}

export const isTemporaryUser = (user: User) => {
  return user.status === UserStatusEnum.TEMPORARY;
};

/**企微关系 */
export enum WecomRelationType {
  /**未加好友 */
  NOT_FOLLOWED = 0,
  /**好友 */
  FOLLOWED = 1,
}

/** 用户 */
export interface User {
  id: number;

  alipayId: string;

  /**小程序 */
  weixinOpenid: string;

  /**服务号 */
  mpOpenid: string;
  weixinUnionid: string;

  moremeCid: number;

  /**企微id */
  wecomId: string;

  /**企微关系 */
  wecomRelationType: WecomRelationType;

  /**微信留存(是否有 weixinOpenid) */
  keepInWechat: boolean;

  nickname: string;

  username: string;

  remarkName: string;

  avatar: string;

  balance: number;

  memo: string;

  mobile: string;

  point: number;

  gender: 0 | 1 | 2;

  status: UserStatusEnum;

  lastVisitTime: string;
  lastLoginIp?: string;

  addTime: string;
  fromUserId: number;
}

export enum FootprintType {
  BaseInfo = 1,
  GoodsDetail = 2,
  Category = 3,
  HOMEPAGE = 4,
}

export interface Footprint {
  id: ID;
  userId: ID;
  poiId: ID;
  type: 1;
  typeId: ID;
  scene: string;
  wx_scene: string;
  memo: string;
  addTime: string;
}

export interface UserDevice {
  benchmarkLevel: number;
  brand: string;
  model: string;
  platform: string;
  system: string;
  userId: number;
  wxSdkVersion: string;
}

export interface VipRule {
  level: number;
  threshold: number;
  name: string;
  iconUrl: string;
  cardUrl: string;
  rightsDesc: string;
  rightsDiscount: number;
}

export const requestUserPointLogList = () =>
  useRequestImmer<{ list: Point[]; page: Page }>(
    {
      method: 'get',
    },
    {
      manual: true,
    }
  );

export const fetchUserBasicInfo = async (username: string) => {
  return (await api.get({
    url: `${UserEndpoint}/basic/username/${username}`,
  })) as User;
};

export const useFetchUserDeviceInfo = () =>
  useRequest<UserDevice>(
    {
      url: `${UserEndpoint}/{username}/device`,
    },
    { manual: true }
  );

export const useFindUser = () => {
  const { send, ...rest } = useRequestImmer<User>(
    {
      url: `${UserEndpoint}/find`,
      method: 'get',
    },
    {
      manual: true,
    }
  );
  return {
    ...rest,
    send: ({ userId, userName, wecomId }: { userId?: number; userName?: string; wecomId?: string }) =>
      send({ params: { userId, userName, wecomId } }),
    sendByUser: (user: Partial<User>) =>
      send({ params: { userId: user.id, userName: user.username, wecomId: user.wecomId } }),
  };
};

export const requestUserBasicInfo = () =>
  useRequestImmer<User, { pathVars: { userId: number } }>(
    {
      url: `${UserEndpoint}/basic/{userId}`,
      method: 'get',
    },
    {
      manual: true,
    }
  );

export const useRequestUserBasicInfo = () =>
  useRequestImmer<User, { pathVars: { username: string } }>(
    {
      url: `${UserEndpoint}/basic/username/{username}`,
      method: 'get',
    },
    {
      manual: true,
    }
  );

export const useUserFullInfo = () => {
  const { send, ...rest } = useRequestImmer<UserFullInfo>(
    {
      url: `${UserEndpoint}/full`,
      method: 'get',
    },
    {
      manual: true,
    }
  );
  return {
    ...rest,
    send: ({ userId, userName, wecomId }: { userId?: number; userName?: string; wecomId?: string }) =>
      send({ params: { userId, userName, wecomId } }),
  };
};

export const batchQueryBasicInfo = async (userIds: number[]) => {
  return (await api.post({ url: `${UserEndpoint}/batchQueryBasicInfo`, data: userIds })) as User[];
};

export const fetchSyncUnionIdByTransactionId = async (data: { username: string; payId: string }) => {
  return (await api.post({
    url: `${UserEndpoint}/syncUnionIdByTransactionId`,
    data,
  })) as User;
};

/**
 * 获取企微当前聊天客户
 * @returns
 */
export const getWecomCurrentUser = async () => {
  return await wecom.getCurExternalContact();
};

export const getUserDisplayNameInternal = (user: User) => {
  if (user == null) {
    return '无效用户';
  }
  return defaultIfEmpty(
    user.remarkName,
    defaultIfEmpty(user.nickname, user.id + (isEmpty(user.mobile) ? '' : '【' + right(user.mobile, 4) + '】'))
  );
};

/**
 * 在 POS 机打开用户界面
 * @param username 
 * @returns 
 */
export const openUserInPos = async (username: string) => {
  return await api.post({
    url: `${UserEndpoint}/openInPos/${username}`
  })
}